import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { PieChart, Wallet, Users, AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react'
import { useCapital } from '@/hooks/useCapital'
import { Button } from '@/components/ui'

// Helper formatter (can be moved to lib later)
const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

export function CapitalPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const { data, isLoading, error, updateSettings, addParticipant, updateParticipant, addContribution, updateContributionStatus } = useCapital(businessId)

  // Quick state for tabs/modals would normally go here. For simplicity we use inline forms.
  const [showSettings, setShowSettings] = useState(false)
  const [settingsForm, setSettingsForm] = useState({ initialCapital: 0, affordableLossPercentage: 40 })

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Memuat data permodalan...</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
        <p>Gagal memuat data: {error}</p>
      </div>
    )
  }

  const { settings, metrics, participants, contributions } = data

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()
    await updateSettings(settingsForm)
    setShowSettings(false)
  }

  const openSettings = () => {
    setSettingsForm({ initialCapital: settings.initialCapital, affordableLossPercentage: settings.affordableLossPercentage })
    setShowSettings(true)
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Kelola Modal (Capital)</h1>
        <p className="text-sm text-slate-400 mt-1">Lacak setoran modal, kepemilikan saham, dan lindungi batas kerugian bisnis.</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Paid Capital */}
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm relative overflow-hidden">
          <div className="absolute -right-4 -top-4 text-slate-800/50"><Wallet size={80} /></div>
          <p className="text-sm text-slate-400 mb-1 relative z-10">Total Modal Disetor (Paid)</p>
          <p className="text-xl font-bold text-slate-100 relative z-10">{formatIDR(metrics.paidCapital)}</p>
          <p className="text-xs text-slate-500 mt-2 relative z-10">Total Committed: {formatIDR(metrics.committedCapital)}</p>
        </div>

        {/* Capital Gap */}
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm relative overflow-hidden">
          <div className="absolute -right-4 -top-4 text-slate-800/50"><PieChart size={80} /></div>
          <p className="text-sm text-slate-400 mb-1 relative z-10">Kekurangan Modal (Gap)</p>
          <p className={`text-xl font-bold relative z-10 ${metrics.capitalGap > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
            {formatIDR(metrics.capitalGap)}
          </p>
          <p className="text-xs text-slate-500 mt-2 relative z-10">Target: {formatIDR(settings.initialCapital)}</p>
        </div>

        {/* Affordable Loss */}
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm relative overflow-hidden">
          <div className="absolute -right-4 -top-4 text-slate-800/50"><ShieldAlert size={80} /></div>
          <div className="flex items-center gap-2 mb-1 relative z-10">
            <p className="text-sm text-slate-400">Affordable Loss</p>
            <span className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">{settings.affordableLossPercentage}%</span>
          </div>
          <p className="text-xl font-bold text-rose-500 relative z-10">{formatIDR(metrics.affordableLossAmount)}</p>
          <p className="text-xs text-slate-500 mt-2 relative z-10">Batas rugi maksimal</p>
        </div>

        {/* Ownership Summary */}
        <div className="p-5 rounded-xl border border-slate-800 bg-slate-900 shadow-sm relative overflow-hidden">
          <div className="absolute -right-4 -top-4 text-slate-800/50"><Users size={80} /></div>
          <p className="text-sm text-slate-400 mb-1 relative z-10">Kepemilikan Dialokasikan</p>
          <p className={`text-xl font-bold relative z-10 ${metrics.unallocatedOwnership < 0 ? 'text-rose-500' : 'text-slate-100'}`}>
            {metrics.totalAllocatedOwnership}%
          </p>
          <p className="text-xs text-slate-500 mt-2 relative z-10">Unallocated: {metrics.unallocatedOwnership}%</p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Col: Participants & Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Settings Section */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-200">Pengaturan Target & Proteksi Modal</h2>
              {!showSettings && (
                <Button variant="outline" size="sm" onClick={openSettings}>Edit Target</Button>
              )}
            </div>

            {showSettings ? (
              <form onSubmit={handleSaveSettings} className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Target Modal Awal (Rp)</label>
                    <input 
                      type="number" 
                      className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-100"
                      value={settingsForm.initialCapital}
                      onChange={e => setSettingsForm(prev => ({...prev, initialCapital: Number(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Affordable Loss (%)</label>
                    <input 
                      type="number" 
                      max="100" min="0" step="0.1"
                      className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-100"
                      value={settingsForm.affordableLossPercentage}
                      onChange={e => setSettingsForm(prev => ({...prev, affordableLossPercentage: Number(e.target.value)}))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowSettings(false)}>Batal</Button>
                  <Button type="submit" size="sm">Simpan</Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col sm:flex-row gap-6 text-sm">
                <div>
                  <span className="text-slate-500 block mb-1">Target Modal (Initial Capital)</span>
                  <span className="font-semibold text-slate-200">{formatIDR(settings.initialCapital)}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">Batas Ekuitas Stop-loss</span>
                  <span className="font-semibold text-rose-400">{formatIDR(metrics.stopLossEquity)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Participants Section */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-200">Partisipan (Founder & Investor)</h2>
              {/* Future: Add modal for new participant here */}
            </div>
            
            {participants.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm border border-dashed border-slate-700 rounded-lg">
                Belum ada partisipan.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400">
                      <th className="pb-2 font-medium">Nama</th>
                      <th className="pb-2 font-medium">Role</th>
                      <th className="pb-2 font-medium text-right">Kepemilikan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {participants.map(p => (
                      <tr key={p.id} className="text-slate-200">
                        <td className="py-3">
                          <div className="font-medium">{p.name}</div>
                          {p.email && <div className="text-xs text-slate-500">{p.email}</div>}
                        </td>
                        <td className="py-3 capitalize">{p.role}</td>
                        <td className="py-3 text-right font-medium">{p.ownershipPercentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Col: Contributions */}
        <div className="lg:col-span-1">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 h-full">
            <h2 className="text-lg font-semibold text-slate-200 mb-4">Riwayat Setoran Modal</h2>
            
            {contributions.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm border border-dashed border-slate-700 rounded-lg">
                Belum ada setoran modal dicatat.
              </div>
            ) : (
              <div className="space-y-4">
                {contributions.map(c => {
                  const participant = participants.find(p => p.id === c.participantId)
                  return (
                    <div key={c.id} className="p-3 bg-slate-800/50 rounded-lg border border-slate-800">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-semibold text-slate-200">{formatIDR(c.amount)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider
                          ${c.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : 
                            c.status === 'committed' ? 'bg-amber-500/10 text-amber-400' : 
                            'bg-slate-500/10 text-slate-400'}
                        `}>
                          {c.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-slate-400">
                        <span>{participant?.name || 'Anonim'} ({c.type})</span>
                        {c.status === 'committed' && (
                          <button 
                            onClick={() => updateContributionStatus(c.id, 'paid')}
                            className="text-emerald-400 hover:text-emerald-300 underline"
                          >
                            Tandai Dibayar
                          </button>
                        )}
                        {c.status === 'paid' && (
                          <button 
                            onClick={() => updateContributionStatus(c.id, 'returned')}
                            className="text-slate-500 hover:text-slate-300 underline"
                          >
                            Return
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}
